import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Sparkles, Link2, Loader2, Check } from "lucide-react";

const AUTOFILL_STEPS = [
  "Connecting to LinkedIn via Apify…",
  "Pulling profile + recent 5 posts…",
  "Analyzing voice with Claude Sonnet 4.5…",
  "Drafting workspace fields…",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState("choose"); // choose / autofill / form
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [autofillStep, setAutofillStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [autofillSamples, setAutofillSamples] = useState([]);
  const [autofillProfile, setAutofillProfile] = useState(null);

  React.useEffect(() => {
    if (!autofilling) return;
    setAutofillStep(0);
    const t = setInterval(() => {
      setAutofillStep((s) => Math.min(s + 1, AUTOFILL_STEPS.length - 1));
    }, 12000);
    return () => clearInterval(t);
  }, [autofilling]);

  const [form, setForm] = useState({
    name: "",
    company: "",
    product: "",
    linkedin_url: "",
    icp_description: "",
    voice_notes: "",
    product_features: [""],
    pain_points: [""],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, i, v) =>
    setForm((f) => ({ ...f, [k]: f[k].map((x, j) => (i === j ? v : x)) }));
  const addItem = (k) => setForm((f) => ({ ...f, [k]: [...f[k], ""] }));
  const removeItem = (k, i) =>
    setForm((f) => ({ ...f, [k]: f[k].filter((_, j) => j !== i) }));

  const runAutofill = async () => {
    if (!linkedinUrl.trim()) {
      toast.error("Paste a LinkedIn profile URL");
      return;
    }
    setAutofilling(true);
    try {
      const r = await apiPost("/workspaces/autofill-from-linkedin", {
        linkedin_url: linkedinUrl.trim(),
      });
      const d = r.draft || {};
      setForm({
        name: d.workspace_name || r.profile?.name || "",
        company: d.company || "",
        product: d.product || "",
        linkedin_url: r.profile?.linkedin_url || linkedinUrl.trim(),
        icp_description: d.icp_description || "",
        voice_notes: d.voice_notes || "",
        product_features: d.product_features?.length ? d.product_features : [""],
        pain_points: d.pain_points?.length ? d.pain_points : [""],
      });
      setAutofillProfile(r.profile || {});
      setAutofillSamples(r.posts || []);
      setStep("form");
      toast.success("Profile analyzed — review and adjust below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Autofill failed — try Manual setup");
    } finally {
      setAutofilling(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Workspace name required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        product_features: form.product_features.filter((x) => x.trim()),
        pain_points: form.pain_points.filter((x) => x.trim()),
      };
      const ws = await apiPost("/workspaces", payload);

      // If we autofilled from LinkedIn, also create a default voice profile + samples
      if (autofillProfile?.linkedin_url) {
        try {
          const vp = await apiPost(`/workspaces/${ws.workspace_id}/voice_profiles`, {
            name: "Personal Voice",
            linkedin_url: autofillProfile.linkedin_url,
            headline: autofillProfile.headline || "",
            voice_notes: form.voice_notes,
            avatar_url: autofillProfile.avatar_url || "",
            is_default: true,
            autofill: false,
          });
          // Add the scraped posts as voice samples
          for (const s of autofillSamples.slice(0, 5)) {
            if (!s.text) continue;
            await apiPost(`/workspaces/${ws.workspace_id}/voice_samples`, {
              sample_text: s.text,
              source: "linkedin_post",
              tone_tags: [],
              voice_profile_id: vp.id,
            }).catch(() => {});
          }
        } catch (_) {
          // non-fatal — workspace already created
        }
      }
      toast.success("Workspace created");
      navigate(`/workspace/${ws.workspace_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create workspace");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout
      title="New Workspace"
      subtitle={step === "choose" ? "step 1 · choose path" : "step 2 · brand profile"}
      actions={
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          data-testid="back-btn"
          className="btn-ghost"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
        </Button>
      }
    >
      {step === "choose" && (
        <div className="max-w-2xl">
          <p className="text-sm text-zinc-400 mb-8 max-w-xl">
            Each workspace = one client brand. You can start fast (paste a LinkedIn
            URL and we'll extract everything) or do it manually.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => setStep("autofill")}
              data-testid="choose-autofill"
              className="surface surface-hover p-6 text-left group"
            >
              <Sparkles className="w-5 h-5 text-sky-400 mb-3" />
              <div className="heading text-xl text-zinc-100 mb-1">Quick start</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Paste a LinkedIn profile URL. We'll fetch their bio + 5 recent
                posts and AI-draft the entire workspace including voice notes.
              </p>
              <div className="overline mt-4" style={{ fontSize: 9 }}>
                takes ~45 seconds
              </div>
            </button>
            <button
              onClick={() => setStep("form")}
              data-testid="choose-manual"
              className="surface surface-hover p-6 text-left group"
            >
              <Plus className="w-5 h-5 text-zinc-400 mb-3" />
              <div className="heading text-xl text-zinc-100 mb-1">Manual</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Fill the brand profile from scratch — use when you can't share
                the client's LinkedIn or want full control from the start.
              </p>
              <div className="overline mt-4" style={{ fontSize: 9 }}>
                you fill everything
              </div>
            </button>
          </div>
        </div>
      )}

      {step === "autofill" && (
        <div className="max-w-2xl">
          <p className="text-sm text-zinc-400 mb-6 max-w-xl">
            Paste a LinkedIn profile URL. We'll pull headline, recent posts, and
            analyze the voice — then drop you into the workspace form prefilled.
          </p>
          <div className="surface p-5 space-y-4">
            <Label className="text-xs text-zinc-300 block">LinkedIn URL</Label>
            <div className="relative">
              <Link2 className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="autofill-url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/<handle>/"
                className="bg-zinc-950 border-zinc-800 text-zinc-100 pl-9"
                onKeyDown={(e) => e.key === "Enter" && !autofilling && runAutofill()}
                disabled={autofilling}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={runAutofill}
                disabled={autofilling}
                className="btn-primary"
                data-testid="autofill-run"
              >
                <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${autofilling ? "animate-pulse" : ""}`} />
                {autofilling ? "Analyzing profile…" : "Analyze & prefill"}
              </Button>
              <button
                onClick={() => setStep("form")}
                className="btn-ghost"
                data-testid="autofill-skip"
                disabled={autofilling}
              >
                Skip — fill manually
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              Uses Apify ($0.05–0.20 per profile) + Claude Sonnet 4.5. Total cost ~$0.10–$0.30.
            </p>

            {autofilling && (
              <div
                data-testid="autofill-progress"
                className="surface p-4 border-sky-400/30 bg-sky-400/5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-4 h-4 text-sky-400 animate-spin shrink-0" />
                  <div className="overline text-sky-400" style={{ fontSize: 9 }}>
                    analyzing — takes 30-60 seconds, do not navigate away
                  </div>
                </div>
                <div className="space-y-1.5">
                  {AUTOFILL_STEPS.map((s, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 text-xs transition-colors ${
                        i < autofillStep
                          ? "text-emerald-400"
                          : i === autofillStep
                          ? "text-zinc-100"
                          : "text-zinc-600"
                      }`}
                    >
                      <span className="w-4 inline-flex justify-center">
                        {i < autofillStep ? (
                          <Check className="w-3 h-3" />
                        ) : i === autofillStep ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        )}
                      </span>
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === "form" && (
        <div className="max-w-3xl">
          {autofillProfile && (
            <div className="surface p-4 mb-6 flex items-center gap-4 fade-up">
              {autofillProfile.avatar_url && (
                <img
                  src={autofillProfile.avatar_url}
                  alt={autofillProfile.name}
                  className="w-12 h-12 rounded-md object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="overline" style={{ fontSize: 9 }}>
                  prefilled from
                </div>
                <div className="text-sm text-zinc-100 truncate">{autofillProfile.name}</div>
                <div className="text-xs text-zinc-500 truncate">{autofillProfile.headline}</div>
              </div>
              <div className="overline text-sky-400" style={{ fontSize: 9 }}>
                {autofillSamples.length} voice samples queued
              </div>
            </div>
          )}
          <p className="text-sm text-zinc-400 mb-8 max-w-xl">
            Review and adjust. You can refine voice samples, ICP signals, and
            knowledge base inside the workspace afterwards.
          </p>

          <div className="space-y-8">
            <Section title="Identity">
              <Field label="Workspace name *">
                <Input
                  data-testid="ws-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Angad / LeanSuite"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                />
              </Field>
              <Field label="Company">
                <Input
                  data-testid="ws-company"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                />
              </Field>
              <Field label="Product">
                <Input
                  data-testid="ws-product"
                  value={form.product}
                  onChange={(e) => set("product", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                />
              </Field>
              <Field label="LinkedIn URL">
                <Input
                  data-testid="ws-linkedin"
                  value={form.linkedin_url}
                  onChange={(e) => set("linkedin_url", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                />
              </Field>
            </Section>

            <Section title="ICP">
              <Field label="ICP description (who is the target customer?)">
                <Textarea
                  data-testid="ws-icp"
                  rows={4}
                  value={form.icp_description}
                  onChange={(e) => set("icp_description", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                />
              </Field>
            </Section>

            <Section title="Voice — how do they write?">
              <Field label="Voice notes (frameworks, style rules, beliefs)">
                <Textarea
                  data-testid="ws-voice"
                  rows={6}
                  value={form.voice_notes}
                  onChange={(e) => set("voice_notes", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                />
              </Field>
            </Section>

            <Section title="Product features (one per line)">
              <ListEditor
                testidPrefix="feature"
                items={form.product_features}
                placeholder="e.g. Searchable failure history"
                onChange={(i, v) => setList("product_features", i, v)}
                onAdd={() => addItem("product_features")}
                onRemove={(i) => removeItem("product_features", i)}
              />
            </Section>

            <Section title="Customer pain points (one per line)">
              <ListEditor
                testidPrefix="pain"
                items={form.pain_points}
                placeholder="e.g. Root cause analysis takes forever"
                onChange={(i, v) => setList("pain_points", i, v)}
                onAdd={() => addItem("pain_points")}
                onRemove={(i) => removeItem("pain_points", i)}
              />
            </Section>

            <div className="pt-4 flex items-center gap-3">
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="create-workspace-submit"
                className="btn-primary"
              >
                {submitting ? "Creating…" : "Create workspace"}
              </Button>
              <button
                onClick={() => setStep("choose")}
                className="btn-ghost"
                data-testid="back-to-choose"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="surface p-5">
      <div className="overline mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-zinc-300 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
function ListEditor({ items, onChange, onAdd, onRemove, placeholder, testidPrefix }) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input
            data-testid={`${testidPrefix}-input-${i}`}
            value={v}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder={placeholder}
            className="bg-zinc-900 border-zinc-800 text-zinc-100 flex-1"
          />
          {items.length > 1 && (
            <button
              onClick={() => onRemove(i)}
              className="btn-ghost"
              data-testid={`${testidPrefix}-remove-${i}`}
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={onAdd}
        className="btn-ghost inline-flex items-center gap-1.5"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}
